/**
 * TeamModal
 *
 * Centered "Add team" / "Edit team" modal matching the May 2026 Figma.
 *
 * Layout (Figma):
 *   ┌─────────── Add team ────────────┐
 *   │ Team name*                      │
 *   │ [ Team name                   ] │
 *   │                                 │
 *   │ Talk group ⓘ                    │
 *   │ [ Select talk group         ▾ ] │
 *   │                                 │
 *   │ Members ⓘ                       │
 *   │ [ Select users              ▾ ] │
 *   │                                 │
 *   │ Home group* ⓘ                   │
 *   │ [ Select home group         ▾ ] │
 *   │                                 │
 *   │              Cancel  [ Add ]    │
 *   └─────────────────────────────────┘
 *
 * Single-pane modal (no left rail) — reuses the same `.os-*` shell as the
 * other settings modals via the `.os-modal-single` modifier.
 *
 * Validation:
 *   • Team name   — required
 *   • Home group  — required (must be one of the selected Talk groups; if
 *                   none chosen the dropdown is disabled)
 */
import { useEffect, useMemo, useRef, useState } from 'react';

const EMPTY = {
  name: '',
  talkGroupIds: [],
  memberIds: [],
  homeGroupId: null,
};

/* ---------- helpers ------------------------------------------------------- */

function Help({ tip }) {
  return (
    <span className="os-help" title={tip} aria-label={tip}>
      <i className="far fa-question-circle"></i>
    </span>
  );
}

function FieldLabel({ children, required, tip }) {
  return (
    <label className="os-label">
      <span>{children}{required && <span className="os-req">*</span>}</span>
      {tip && <Help tip={tip} />}
    </label>
  );
}

function userIdOf(u)   { return u?.userId ?? u?.id; }
function userNameOf(u) {
  const fn = u?.firstName || ''; const ln = u?.lastName || '';
  const full = `${fn} ${ln}`.trim();
  return full || u?.email || u?.username || `User ${userIdOf(u)}`;
}
function tgIdOf(g)   { return g?.talkGroupId ?? g?.id; }
function tgNameOf(g) { return g?.name || g?.talkGroupName || `Talk group ${tgIdOf(g)}`; }

/* ---------- Generic multi-select dropdown -------------------------------- */

function MultiSelect({
  options,
  selected,
  onChange,
  placeholder,
  getId,
  getLabel,
  searchPlaceholder = 'Search',
}) {
  const [open, setOpen]   = useState(false);
  const [query, setQuery] = useState('');
  const wrapRef = useRef(null);
  const masterRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return q ? options.filter((o) => getLabel(o).toLowerCase().includes(q)) : options;
  }, [options, query, getLabel]);

  const allIds = useMemo(() => options.map(getId), [options, getId]);
  const allSel = options.length > 0 && selected.length === options.length;
  const someSel = selected.length > 0 && !allSel;

  useEffect(() => {
    if (masterRef.current) masterRef.current.indeterminate = someSel;
  }, [someSel]);

  const toggleAll = () => onChange(allSel ? [] : allIds);
  const toggleOne = (id) =>
    onChange(selected.includes(id) ? selected.filter((x) => x !== id) : [...selected, id]);

  const summary = useMemo(() => {
    if (selected.length === 0) return '';
    return selected
      .map((id) => options.find((o) => getId(o) === id))
      .filter(Boolean)
      .map(getLabel)
      .join(', ');
  }, [selected, options, getId, getLabel]);

  return (
    <div className={`sites-mselect ${open ? 'open' : ''}`} ref={wrapRef}>
      <div
        role="button"
        tabIndex={0}
        className="sites-mselect-trigger tg-sites-trigger"
        onClick={() => setOpen((v) => !v)}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setOpen((v) => !v); } }}
      >
        <div className="sites-mselect-summary">
          {selected.length === 0
            ? <span className="sites-mselect-placeholder">{placeholder}</span>
            : <span className="sites-mselect-summary-text">{summary}</span>}
        </div>
        <i className="fas fa-chevron-down sites-mselect-caret" aria-hidden="true" />
      </div>

      {open && (
        <div className="sites-mselect-menu tg-sites-menu" role="listbox">
          <div className="tg-sites-search">
            <i className="fas fa-search"></i>
            <input
              type="text"
              autoFocus
              placeholder={searchPlaceholder}
              value={query}
              onChange={(e) => setQuery(e.target.value)}
            />
          </div>

          {options.length === 0 ? (
            <div className="sites-mselect-empty">No options available</div>
          ) : (
            <>
              <label className="sites-mselect-row sites-mselect-row-all">
                <input ref={masterRef} type="checkbox" checked={allSel} onChange={toggleAll} />
                <span className="sites-mselect-box" aria-hidden="true">
                  {allSel && <i className="fas fa-check" />}
                  {someSel && !allSel && <i className="fas fa-minus" />}
                </span>
                <span className="sites-mselect-label sites-mselect-label-strong">Select all</span>
              </label>

              {filtered.length === 0 && (
                <div className="sites-mselect-empty">No matches for "{query}"</div>
              )}

              {filtered.map((o) => {
                const id = getId(o);
                const checked = selected.includes(id);
                return (
                  <label key={id} className="sites-mselect-row">
                    <input type="checkbox" checked={checked} onChange={() => toggleOne(id)} />
                    <span className="sites-mselect-box" aria-hidden="true">
                      {checked && <i className="fas fa-check" />}
                    </span>
                    <span className="sites-mselect-label">
                      <span className="sites-mselect-name">{getLabel(o)}</span>
                    </span>
                  </label>
                );
              })}
            </>
          )}
        </div>
      )}
    </div>
  );
}

/* ---------- Modal --------------------------------------------------------- */

export default function TeamModal({
  open,
  initial,
  talkGroups = [],
  users = [],
  saving = false,
  onClose,
  onSave,
}) {
  const [form, setForm] = useState(EMPTY);
  const [showErrors, setShowErrors] = useState(false);
  const isEdit = !!initial;

  useEffect(() => {
    if (!open) return;
    setForm(initial ? { ...EMPTY, ...initial } : EMPTY);
    setShowErrors(false);
  }, [open, initial]);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  // Home group must be one of the selected talk groups.
  const homeOptions = useMemo(
    () => talkGroups.filter((g) => form.talkGroupIds.includes(tgIdOf(g))),
    [talkGroups, form.talkGroupIds],
  );

  // If selected home group is removed from talk groups, clear it.
  useEffect(() => {
    if (form.homeGroupId != null && !form.talkGroupIds.includes(form.homeGroupId)) {
      setForm((f) => ({ ...f, homeGroupId: null }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.talkGroupIds]);

  // IMPORTANT: keep this AFTER all hooks (Rules of Hooks).
  if (!open) return null;

  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const nameErr = showErrors && !form.name?.trim();
  const homeErr = showErrors && (form.homeGroupId == null);

  const handleSubmit = () => {
    setShowErrors(true);
    if (!form.name?.trim()) return;
    if (form.homeGroupId == null) return;
    onSave(form);
  };

  return (
    <div
      className="os-modal-overlay"
      role="dialog"
      aria-modal="true"
      aria-label={isEdit ? 'Edit team' : 'Add team'}
      onMouseDown={(e) => e.target === e.currentTarget && onClose()}
    >
      <div className="os-modal os-modal-single" onClick={(e) => e.stopPropagation()}>
        <header className="os-modal-header">
          <h2>{isEdit ? 'Edit team' : 'Add team'}</h2>
        </header>

        <div className="os-modal-body tm-modal-body">
          <section className="os-pane tm-pane">
            {/* Team name */}
            <div className="os-field">
              <FieldLabel required>Team name</FieldLabel>
              <input
                type="text"
                className={`os-input ${nameErr ? 'os-input-error' : ''}`}
                placeholder="Team name"
                value={form.name}
                onChange={(e) => setField('name', e.target.value)}
              />
              {nameErr && (
                <div className="os-field-error">
                  <i className="fas fa-info-circle"></i> This field is required
                </div>
              )}
            </div>

            {/* Talk groups (multi) */}
            <div className="os-field">
              <FieldLabel tip="Talk groups associated with this team.">Talk group</FieldLabel>
              <MultiSelect
                options={talkGroups}
                selected={form.talkGroupIds}
                onChange={(ids) => setField('talkGroupIds', ids)}
                placeholder="Select talk group"
                getId={tgIdOf}
                getLabel={tgNameOf}
                searchPlaceholder="Search talk groups"
              />
            </div>

            {/* Members (multi) */}
            <div className="os-field">
              <FieldLabel tip="Users that belong to this team.">Members</FieldLabel>
              <MultiSelect
                options={users}
                selected={form.memberIds}
                onChange={(ids) => setField('memberIds', ids)}
                placeholder="Select users"
                getId={userIdOf}
                getLabel={userNameOf}
                searchPlaceholder="Search users"
              />
            </div>

            {/* Home group (single, picked from the selected talk groups) */}
            <div className="os-field">
              <FieldLabel required tip="Default talk group for members of this team.">
                Home group
              </FieldLabel>
              <div className={`tm-select-wrap ${homeErr ? 'tm-select-error' : ''}`}>
                <select
                  className="os-input tm-select"
                  value={form.homeGroupId ?? ''}
                  onChange={(e) =>
                    setField('homeGroupId',
                      e.target.value === '' ? null : (Number.isNaN(Number(e.target.value)) ? e.target.value : Number(e.target.value)))
                  }
                  disabled={homeOptions.length === 0}
                >
                  <option value="">
                    {homeOptions.length === 0
                      ? 'Select talk group(s) first'
                      : 'Select home group'}
                  </option>
                  {homeOptions.map((g) => (
                    <option key={tgIdOf(g)} value={tgIdOf(g)}>{tgNameOf(g)}</option>
                  ))}
                </select>
                <i className="fas fa-chevron-down tm-select-caret" aria-hidden="true" />
              </div>
              {homeErr && (
                <div className="os-field-error">
                  <i className="fas fa-info-circle"></i> This field is required
                </div>
              )}
            </div>
          </section>
        </div>

        <footer className="os-modal-footer">
          <button type="button" className="btn-ghost-small" onClick={onClose} disabled={saving}>
            Cancel
          </button>
          <button
            type="button"
            className="btn-primary-pill"
            onClick={handleSubmit}
            disabled={saving}
          >
            {saving ? 'Saving…' : (isEdit ? 'Save' : 'Add')}
          </button>
        </footer>
      </div>
    </div>
  );
}

